import { parseDateOnly } from './dateHelpers';

interface Payment {
  date: string;
  amount: number;
}

/**
 * Calculate membership status as of a specific date
 * @param payments - Array of payment records
 * @param asOfDate - Date to calculate status for (defaults to today)
 * @returns Membership status: 'Active', 'Inactive', or 'Pending'
 *
 * Rules:
 * - Payments made Oct-Dec count for BOTH the current year AND the next year
 * - Member is active if total contributions >= $25 for the year
 * - Membership period: Oct 1 through end of following year (15 months)
 */
export const calculateMembershipStatus = (
  payments: Payment[],
  asOfDate?: Date
): 'Active' | 'Inactive' | 'Pending' => {
  if (!payments || payments.length === 0) {
    return 'Pending';
  }

  // Default to today if no date provided
  const checkDate = asOfDate || new Date();
  const checkYear = checkDate.getFullYear();

  let yearTotal = 0;

  payments.forEach(payment => {
    const paymentDate = parseDateOnly(payment.date);
    const paymentYear = paymentDate.getFullYear();
    const paymentMonth = paymentDate.getMonth(); // 0-indexed: 0=Jan, 9=Oct, 11=Dec

    // Only consider payments made on or before the check date
    if (paymentDate <= checkDate) {
      // Check if payment is in Oct-Dec of the prior year (counts for checkYear)
      if (paymentYear === checkYear - 1 && paymentMonth >= 9) {
        yearTotal += payment.amount;
      }
      // Check if payment is in the check year (Jan-Dec)
      else if (paymentYear === checkYear) {
        yearTotal += payment.amount;
      }
    }
  });

  if (yearTotal >= 25) {
    return 'Active';
  } else if (yearTotal > 0) {
    return 'Inactive';
  } else {
    return 'Pending';
  }
};

/**
 * Get all members who are active as of a specific date
 * @param members - Array of members with their payment records
 * @param asOfDate - Date to calculate status for (defaults to Dec 31 of prior year)
 * @returns Array of active member IDs
 */
export const getActiveMembersAsOf = (
  members: Array<{ id: string; payments: Payment[] }>,
  asOfDate?: Date
): string[] => {
  return members
    .filter(member => calculateMembershipStatus(member.payments, asOfDate) === 'Active')
    .map(member => member.id);
};

/**
 * Calculate the membership year a payment contributes to
 * @param paymentDate - Date of the payment
 * @returns Array of years this payment counts towards
 *
 * Rules:
 * - Payments made Oct-Dec count for BOTH current year AND next year
 * - Other payments count only for their calendar year
 */
export const getPaymentMembershipYears = (paymentDate: Date): number[] => {
  const year = paymentDate.getFullYear();
  const month = paymentDate.getMonth(); // 0-indexed

  // Oct-Dec payments (months 9, 10, 11) count for both current and next year
  if (month >= 9) {
    return [year, year + 1];
  }

  // Other payments count only for their year
  return [year];
}; 